import React, { ChangeEvent, useState, useRef } from 'react';
import { Box, Typography, Paper, IconButton, Input } from '@mui/material';
import { AttachFile, Close } from '@mui/icons-material';
import _ from 'lodash';
import { labelStyles, iconStyles, fileListStyles, fileItemStyles, extraFilesStyles, errorStyles } from './fileInputStyles';
import { JsonifiedFile, JsonifyFile } from '../../utility/functions';
import { getFileTypeIcon } from '../../utility/Attachment';

interface IFileInputProps {
  onFilesChange: (files: JsonifiedFile[]) => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const MAX_VISIBLE_FILES = 3;
const MAX_TOTAL_FILES = 100;

export default function FileInput({ onFilesChange }: IFileInputProps) {
  const [files, setFiles] = useState<JsonifiedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = (fileList: FileList) => {
    // Check total file limit
    if (files.length + fileList.length > MAX_TOTAL_FILES) {
      setError(`You can only upload up to ${MAX_TOTAL_FILES} files.`);
      return false;
    }

    // Check file size limits
    for (let i = 0; i < fileList.length; i++) {
      if (fileList[i].size > MAX_FILE_SIZE) {
        setError(`File "${fileList[i].name}" exceeds the 50 MB size limit.`);
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    if (!validateFiles(selectedFiles)) return;

    const newFiles = await Promise.all(_.map(selectedFiles, JsonifyFile));
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const fileList = event.dataTransfer.files;
    if (!validateFiles(fileList)) return;

    const newFiles = await Promise.all(_.map(fileList, JsonifyFile));
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = _.filter(files, (_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const visibleFiles = _.slice(files, 0, MAX_VISIBLE_FILES);
  const extraFileCount = files.length - MAX_VISIBLE_FILES;

  return (
    <Box sx={{ mt: 2 }}>
      <Box component="label" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} sx={labelStyles(isDragging)}>
        <AttachFile sx={iconStyles(isDragging)} />
        <Typography variant="body2" sx={{ color: (theme) => (isDragging ? theme.palette.primary.main : theme.palette.text.secondary) }}>
          {isDragging ? 'Drop files here' : 'Drag and drop files or click to attach'}
        </Typography>
        <Input type="file" inputProps={{ multiple: true }} sx={{ display: 'none' }} ref={fileInputRef} onChange={handleFileSelect} />
      </Box>

      {error && <Typography sx={errorStyles}>{error}</Typography>}

      {files.length > 0 && (
        <Paper variant="outlined" sx={fileListStyles}>
          {_.map(visibleFiles, (file, index) => (
            <Box key={index} sx={fileItemStyles}>
              <Box sx={fileItemStyles}>
                <Box>{getFileTypeIcon(file.contentType)}</Box>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {file.name}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                <Close />
              </IconButton>
            </Box>
          ))}
          {extraFileCount > 0 && (
            <Typography sx={extraFilesStyles}>
              +{extraFileCount} more file{extraFileCount > 1 ? 's' : ''}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
